import LoadingSpinner from '@carvana/loading-spinner';
import { createComponent } from '@carvana/merch-components';
import classNames from 'classnames';
import { useContext } from 'react';
import { EpicSection, SectionHeader } from '@/vdp/styles';
import Sections from '~/constants/vdp/sections';
import TradeContext from '~/context/common/TradeContext';
import UserContext, { useIsLoggedIn } from '~/context/common/UserContext';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import useShouldDisplayOrderStatus from '~/hooks/vdp/selectors/useShouldDisplayOrderStatus';
import useShouldShowPriceDetails from '~/hooks/vdp/selectors/useShouldShowPriceDetails';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import StatusCard from './OrderStatusCard/StatusCard';
import SptPriceDetailsContent from './SptPriceDetailsContent/SptPriceDetailsContent';
import SptPriceDetailsData from './SptPriceDetailsContent/SptPriceDetailsData';
import SptPriceDetailsContext, {
  SptPriceDetailsContextProvider
} from './SptPriceDetailsContext';

const PriceDetailsContainer = createComponent('div', {
  className: 'mx-auto w-full px-24 l:px-0'
});

const OrderStatusContainer = createComponent('div', {
  className: 'mx-auto max-w-[1144px] px-24 xl:px-0'
});

export const LoaderWrapper = createComponent('div', {
  // matches size of placeholder in VDP
  className: 'h-[1176px] m:h-[668px]'
});

type OwnProps = {
  whiteBg?: boolean;
};

export function SptPriceDetails({ whiteBg }: OwnProps) {
  const {
    rcbPurchasing: { isLoaded: isPurchasingLoaded }
  } = useContext(UserVehicleContext);
  const {
    creditStatus: { isLoaded: isCreditStatusLoaded }
  } = useContext(SptPriceDetailsContext);
  const {
    trades: { isLoaded: isTradesLoaded }
  } = useContext(TradeContext);
  const {
    userState: { isLoaded: userIsLoaded }
  } = useContext(UserContext);
  const isLoggedIn = useIsLoggedIn();
  const shouldDisplayOrderStatus = useShouldDisplayOrderStatus();
  const isLoading = isLoggedIn
    ? !userIsLoaded ||
      !isTradesLoaded ||
      !isCreditStatusLoaded ||
      !isPurchasingLoaded
    : !userIsLoaded;

  return (
    <EpicSection
      className={classNames(Sections.General, {
        'py-40 l:py-64': shouldDisplayOrderStatus
      })}
      data-qa="Spt Price Details"
      data-nav-section={Sections.PriceDetails}
      whiteBg={whiteBg}
    >
      <SectionHeader
        className={classNames({
          hidden: shouldDisplayOrderStatus
        })}
      >
        Price Details
      </SectionHeader>
      {(() => {
        switch (true) {
          case isLoading:
            return (
              <LoaderWrapper>
                <LoadingSpinner />
              </LoaderWrapper>
            );
          case shouldDisplayOrderStatus:
            return (
              <OrderStatusContainer>
                <StatusCard />
              </OrderStatusContainer>
            );
          default:
            return (
              <PriceDetailsContainer>
                <SptPriceDetailsContent />
              </PriceDetailsContainer>
            );
        }
      })()}
    </EpicSection>
  );
}

function SptPriceDetailsWrapper() {
  const shouldShowPriceDetails = useShouldShowPriceDetails();

  return shouldShowPriceDetails ? (
    <SptPriceDetailsContextProvider>
      <SptPriceDetailsData />
      <SptPriceDetails />
    </SptPriceDetailsContextProvider>
  ) : null;
}

export default withErrorBoundary(
  SptPriceDetailsWrapper,
  undefined,
  undefined,
  Sections.PriceDetails
);
